import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, DollarSign, Music, MessageSquare, Shield, Crown, Mic, 
  ArrowLeft, RefreshCw, Ban, Trash2, ChevronDown, ChevronUp,
  Loader2, AlertCircle, CheckCircle, User, Megaphone, Plus, Edit2, X, ExternalLink,
  BarChart3, TrendingUp, Clock, Activity, Settings, Radio, Link, Image, Video, Newspaper,
  Wifi, WifiOff, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [stationConfig, setStationConfig] = useState(null);
  const [stationHealth, setStationHealth] = useState(null);
  const [healthRefreshing, setHealthRefreshing] = useState(false);
  const [adminRefreshing, setAdminRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [configSaved, setConfigSaved] = useState(false);
  
  // Sponsor form state
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({
    name: '',
    tagline: '',
    logo_url: '',
    website_url: '',
    is_active: true
  });

  // News form state
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({
    text: '',
    link_url: '',
    is_active: true,
    priority: 0
  });
  const [rssUrl, setRssUrl] = useState('');
  const [rssPreview, setRssPreview] = useState(null);
  const [rssLoading, setRssLoading] = useState(false);

  // Check if user is admin/mod
  const isAdmin = user?.role === 'admin';
  const isMod = user?.role === 'mod';
  const canAccess = isAdmin || isMod;

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 403) {
        setError('Access denied. Admin privileges required.');
      }
    } catch (err) {
      setError('Failed to load stats');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (err) {
      console.log('Failed to load users');
    }
  }, []);

  const fetchDonations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/donations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDonations(data.donations);
      }
    } catch (err) {
      console.log('Failed to load donations');
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/chat/messages`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (err) {
      console.log('Failed to load messages');
    }
  }, []);

  const fetchSponsors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sponsors`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSponsors(data);
      }
    } catch (err) {
      console.log('Failed to load sponsors');
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.log('Failed to load analytics');
    }
  }, []);

  const fetchStationConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/station/config`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStationConfig(data);
        setRssUrl(data.rss_feed_url || '');
      }
    } catch (err) {
      console.log('Failed to load station config');
    }
  }, []);

  const fetchNews = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/news`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNewsItems(data);
      }
    } catch (err) {
      console.log('Failed to load news');
    }
  }, []);

  const fetchStationHealth = useCallback(async (isManual = false) => {
    if (isManual) setHealthRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/station/health`);
      if (response.ok) {
        const data = await response.json();
        setStationHealth(data);
      }
    } catch (err) {
      console.log('Failed to load station health');
    } finally {
      if (isManual) setHealthRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchUsers(), fetchDonations(), fetchMessages(), fetchSponsors(), fetchAnalytics(), fetchStationConfig(), fetchNews(), fetchStationHealth()]);
      setLoading(false);
    };
    if (canAccess) {
      loadAll();
    } else {
      setLoading(false);
    }
  }, [canAccess, fetchStats, fetchUsers, fetchDonations, fetchMessages, fetchSponsors, fetchAnalytics, fetchStationConfig, fetchNews, fetchStationHealth]);

  // Auto-refresh station health every 30 seconds
  useEffect(() => {
    if (!canAccess) return;
    const interval = setInterval(fetchStationHealth, 30000);
    return () => clearInterval(interval);
  }, [canAccess, fetchStationHealth]);

  const handleRoleChange = async (userId, newRole) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      console.log('Failed to update role');
    }
    setActionLoading(null);
  };

  const handleBan = async (userId, banned) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/ban`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ banned })
      });
      if (response.ok) {
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      console.log('Failed to ban user');
    }
    setActionLoading(null);
  };

  const handleDeleteMessage = async (messageId) => {
    setActionLoading(messageId);
    try {
      const response = await fetch(`${API_URL}/api/admin/chat/${messageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchMessages();
        fetchStats();
      }
    } catch (err) {
      console.log('Failed to delete message');
    }
    setActionLoading(null);
  };

  // Sponsor CRUD handlers
  const resetSponsorForm = () => {
    setSponsorForm({ name: '', tagline: '', logo_url: '', website_url: '', is_active: true });
    setEditingSponsor(null);
    setShowSponsorForm(false);
  };

  const handleSponsorSubmit = async (e) => {
    e.preventDefault();
    setActionLoading('sponsor-form');
    
    try {
      if (editingSponsor) {
        // Update existing sponsor
        const response = await fetch(`${API_URL}/api/admin/sponsors/${editingSponsor.sponsor_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(sponsorForm)
        });
        if (response.ok) {
          fetchSponsors();
          resetSponsorForm();
        }
      } else {
        // Create new sponsor
        const response = await fetch(`${API_URL}/api/admin/sponsors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(sponsorForm)
        });
        if (response.ok) {
          fetchSponsors();
          resetSponsorForm();
        }
      }
    } catch (err) {
      console.log('Failed to save sponsor');
    }
    setActionLoading(null);
  };

  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setSponsorForm({
      name: sponsor.name,
      tagline: sponsor.tagline || '',
      logo_url: sponsor.logo_url || '',
      website_url: sponsor.website_url || '',
      is_active: sponsor.is_active
    });
    setShowSponsorForm(true);
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) return;
    
    setActionLoading(sponsorId);
    try {
      const response = await fetch(`${API_URL}/api/admin/sponsors/${sponsorId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchSponsors();
      }
    } catch (err) {
      console.log('Failed to delete sponsor');
    }
    setActionLoading(null);
  };

  const handleToggleSponsorActive = async (sponsor) => {
    setActionLoading(sponsor.sponsor_id);
    try {
      const response = await fetch(`${API_URL}/api/admin/sponsors/${sponsor.sponsor_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !sponsor.is_active })
      });
      if (response.ok) {
        fetchSponsors();
      }
    } catch (err) {
      console.log('Failed to toggle sponsor');
    }
    setActionLoading(null);
  };

  const handleSaveStationConfig = async (e) => {
    e.preventDefault();
    setActionLoading('station-config');
    setConfigSaved(false);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/station/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(stationConfig)
      });
      if (response.ok) {
        setConfigSaved(true);
        setTimeout(() => setConfigSaved(false), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save config');
      }
    } catch (err) {
      setError('Failed to save station config');
    }
    setActionLoading(null);
  };

  // News CRUD handlers
  const resetNewsForm = () => {
    setNewsForm({ text: '', link_url: '', is_active: true, priority: 0 });
    setEditingNews(null);
    setShowNewsForm(false);
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    setActionLoading('news-form');
    
    try {
      if (editingNews) {
        const response = await fetch(`${API_URL}/api/admin/news/${editingNews.news_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newsForm)
        });
        if (response.ok) {
          fetchNews();
          resetNewsForm();
        }
      } else {
        const response = await fetch(`${API_URL}/api/admin/news`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newsForm)
        });
        if (response.ok) {
          fetchNews();
          resetNewsForm();
        }
      }
    } catch (err) {
      console.log('Failed to save news');
    }
    setActionLoading(null);
  };

  const handleEditNews = (news) => {
    setEditingNews(news);
    setNewsForm({
      text: news.text,
      link_url: news.link_url || '',
      is_active: news.is_active,
      priority: news.priority || 0
    });
    setShowNewsForm(true);
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Delete this news item?')) return;
    
    setActionLoading(newsId);
    try {
      const response = await fetch(`${API_URL}/api/admin/news/${newsId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchNews();
      }
    } catch (err) {
      console.log('Failed to delete news');
    }
    setActionLoading(null);
  };

  const handleToggleNewsActive = async (news) => {
    setActionLoading(news.news_id);
    try {
      const response = await fetch(`${API_URL}/api/admin/news/${news.news_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !news.is_active })
      });
      if (response.ok) {
        fetchNews();
      }
    } catch (err) {
      console.log('Failed to toggle news');
    }
    setActionLoading(null);
  };

  // RSS handlers
  const handlePreviewRss = async () => {
    if (!rssUrl) return;
    setRssLoading(true);
    setRssPreview(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/news/rss-preview?url=${encodeURIComponent(rssUrl)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRssPreview(data);
      }
    } catch (err) {
      setRssPreview({ error: 'Failed to fetch RSS feed' });
    }
    setRssLoading(false);
  };

  const handleSaveRssUrl = async () => {
    setActionLoading('rss-save');
    try {
      const response = await fetch(`${API_URL}/api/admin/news/rss-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rss_feed_url: rssUrl })
      });
      if (response.ok) {
        setRssPreview({ ...rssPreview, saved: true });
        setTimeout(() => setRssPreview(prev => prev ? { ...prev, saved: false } : null), 3000);
      }
    } catch (err) {
      console.log('Failed to save RSS URL');
    }
    setActionLoading(null);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4" style={{ color: '#FFD700' }} />;
      case 'mod': return <Shield className="w-4 h-4" style={{ color: '#22C55E' }} />;
      case 'dj': return <Mic className="w-4 h-4" style={{ color: '#A855F7' }} />;
      default: return <User className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <p style={{ color: 'var(--text-main)' }}>Please log in to access admin panel</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: 'var(--primary)', color: 'white' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <Shield className="w-12 h-12 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <p style={{ color: 'var(--text-main)' }}>Admin access required</p>
          <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
            Current role: {user.role}
          </p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: 'var(--primary)', color: 'white' }}
          >
            Back to Player
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      {/* Background */}
      <div 
        className="fixed inset-0 opacity-20"
        style={{
          background: `radial-gradient(ellipse at 20% 50%, var(--primary) 0%, transparent 50%),
                       radial-gradient(ellipse at 80% 80%, var(--secondary) 0%, transparent 50%)`
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 rounded-lg transition-colors hover:bg-white/10"
              style={{ color: 'var(--text-muted)' }}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 
                className="text-3xl font-bold"
                style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
              >
                Admin Dashboard
              </h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {isAdmin ? 'Full Admin Access' : 'Moderator Access'}
              </p>
            </div>
          </div>
          <button
            onClick={async () => { 
              setAdminRefreshing(true);
              await Promise.all([
                fetchStats(), 
                fetchUsers(), 
                fetchDonations(), 
                fetchMessages(), 
                fetchSponsors(), 
                fetchAnalytics(), 
                fetchStationConfig(), 
                fetchNews(), 
                fetchStationHealth()
              ]);
              setAdminRefreshing(false);
            }}
            disabled={adminRefreshing}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors hover:bg-white/10 disabled:opacity-50"
            style={{ color: 'var(--text-muted)' }}
          >
            <RefreshCw className={`w-4 h-4 ${adminRefreshing ? 'animate-spin' : ''}`} />
            {adminRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['overview', 'analytics', 'users', 'donations', 'chat', 'sponsors', 'news', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
              style={{
                backgroundColor: activeTab === tab ? 'var(--primary)' : 'var(--surface)',
                color: activeTab === tab ? 'white' : 'var(--text-muted)'
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Users</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  {stats.users.total}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {stats.users.djs} DJs • {stats.users.mods} Mods • {stats.users.banned} Banned
                </p>
              </div>

              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5" style={{ color: '#22C55E' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Donations</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: '#22C55E' }}>
                  ${stats.donations.total_amount.toFixed(2)}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {stats.donations.count} donations
                </p>
              </div>

              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Music className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Requests</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  {stats.requests.total}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {stats.requests.pending} pending • {stats.requests.played} played
                </p>
              </div>

              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-5 h-5" style={{ color: '#F59E0B' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Messages</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  {stats.chat.total_messages}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  total chat messages
                </p>
              </div>
            </div>

          {/* Station Health Card */}
          {stationHealth && (
            <div className="glass rounded-2xl p-4 mt-4" data-testid="station-health-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  <h3 className="font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    Station Health
                  </h3>
                </div>
                <button 
                  onClick={() => fetchStationHealth(true)}
                  disabled={healthRefreshing}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                  title="Refresh health status"
                >
                  <RefreshCw className={`w-4 h-4 ${healthRefreshing ? 'animate-spin' : ''}`} style={{ color: 'var(--text-muted)' }} />
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Stream Status */}
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: stationHealth.stream_status === 'online' 
                        ? 'rgba(34, 197, 94, 0.2)' 
                        : 'rgba(239, 68, 68, 0.2)' 
                    }}
                  >
                    {stationHealth.stream_status === 'online' ? (
                      <Wifi className="w-5 h-5" style={{ color: '#22C55E' }} />
                    ) : (
                      <WifiOff className="w-5 h-5" style={{ color: '#EF4444' }} />
                    )}
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Stream</p>
                    <p className="font-semibold" style={{ 
                      color: stationHealth.stream_status === 'online' ? '#22C55E' : '#EF4444' 
                    }}>
                      {stationHealth.stream_status === 'online' ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>

                {/* API Latency */}
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: stationHealth.api_latency_ms < 500 
                        ? 'rgba(34, 197, 94, 0.2)' 
                        : stationHealth.api_latency_ms < 1000
                          ? 'rgba(245, 158, 11, 0.2)'
                          : 'rgba(239, 68, 68, 0.2)'
                    }}
                  >
                    <Zap className="w-5 h-5" style={{ 
                      color: stationHealth.api_latency_ms < 500 
                        ? '#22C55E' 
                        : stationHealth.api_latency_ms < 1000 ? '#F59E0B' : '#EF4444' 
                    }} />
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>API Latency</p>
                    <p className="font-semibold" style={{ color: 'var(--text-main)' }}>
                      {stationHealth.api_latency_ms}ms
                    </p>
                  </div>
                </div>

                {/* Current Listeners */}
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
                  >
                    <Users className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Listeners</p>
                    <p className="font-semibold" style={{ color: 'var(--text-main)' }}>
                      {stationHealth.current_listeners}
                    </p>
                  </div>
                </div>

                {/* Current Song */}
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
                  >
                    <Music className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Now Playing</p>
                    {stationHealth.current_song ? (
                      <p 
                        className="font-semibold truncate" 
                        style={{ color: 'var(--text-main)' }}
                        title={`${stationHealth.current_song.title} - ${stationHealth.current_song.artist}`}
                      >
                        {stationHealth.current_song.title}
                      </p>
                    ) : (
                      <p className="font-semibold" style={{ color: 'var(--text-muted)' }}>-</p>
                    )}
                  </div>
                </div>
              </div>
              
              <p className="text-xs mt-3" style={{ color: 'var(--text-muted)' }}>
                Last checked: {new Date(stationHealth.last_check).toLocaleTimeString()}
              </p>
            </div>
          )}
          </>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Engagement Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-5 h-5" style={{ color: '#22C55E' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Chat Rate</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: '#22C55E' }}>
                  {analytics.engagement.chat_rate}%
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {analytics.engagement.active_chatters} of {analytics.engagement.total_users} users
                </p>
              </div>
              
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Music className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Request Rate</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--primary)' }}>
                  {analytics.engagement.request_rate}%
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {analytics.engagement.requesters} users requested songs
                </p>
              </div>
              
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5" style={{ color: '#F59E0B' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Donor Rate</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: '#F59E0B' }}>
                  {analytics.engagement.donation_rate}%
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {analytics.engagement.donors} users donated
                </p>
              </div>
              
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>User Roles</span>
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  {analytics.role_breakdown.users}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {analytics.role_breakdown.djs} DJs • {analytics.role_breakdown.mods} Mods
                </p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Daily Messages Chart */}
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  <h3 className="font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    Daily Chat Messages (7 days)
                  </h3>
                </div>
                <div className="flex items-end gap-2 h-32">
                  {analytics.daily_messages.map((day, i) => {
                    const max = Math.max(...analytics.daily_messages.map(d => d.count), 1);
                    const height = (day.count / max) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full rounded-t transition-all"
                          style={{ 
                            height: `${height}%`, 
                            minHeight: day.count > 0 ? '4px' : '2px',
                            backgroundColor: 'var(--primary)' 
                          }}
                          title={`${day.count} messages`}
                        />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{day.date.split(' ')[1]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Daily Donations Chart */}
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5" style={{ color: '#22C55E' }} />
                  <h3 className="font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    Daily Donations (7 days)
                  </h3>
                </div>
                <div className="flex items-end gap-2 h-32">
                  {analytics.daily_donations.map((day, i) => {
                    const max = Math.max(...analytics.daily_donations.map(d => d.amount), 1);
                    const height = (day.amount / max) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full rounded-t transition-all"
                          style={{ 
                            height: `${height}%`, 
                            minHeight: day.amount > 0 ? '4px' : '2px',
                            backgroundColor: '#22C55E' 
                          }}
                          title={`$${day.amount.toFixed(2)}`}
                        />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{day.date.split(' ')[1]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Peak Hours */}
            <div className="glass rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5" style={{ color: '#F59E0B' }} />
                <h3 className="font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  Peak Activity Hours (Last 7 days)
                </h3>
              </div>
              <div className="flex items-end gap-1 h-24 overflow-x-auto">
                {analytics.hourly_activity.map((hour, i) => {
                  const max = Math.max(...analytics.hourly_activity.map(h => h.activity), 1);
                  const height = (hour.activity / max) * 100;
                  return (
                    <div key={i} className="flex flex-col items-center gap-1 min-w-[30px]">
                      <div 
                        className="w-4 rounded-t transition-all"
                        style={{ 
                          height: `${height}%`, 
                          minHeight: hour.activity > 0 ? '4px' : '2px',
                          backgroundColor: hour.activity > max * 0.7 ? '#F59E0B' : 'var(--primary)' 
                        }}
                        title={`${hour.activity} messages at ${hour.hour}`}
                      />
                      <span className="text-xs" style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
                        {hour.hour.split(':')[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Lists */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Top Chatters */}
              <div className="glass rounded-2xl p-4">
                <h3 className="font-bold mb-4" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  <MessageSquare className="w-5 h-5 inline mr-2" style={{ color: 'var(--primary)' }} />
                  Top Chatters (30 days)
                </h3>
                <div className="space-y-2">
                  {analytics.top_chatters.length > 0 ? analytics.top_chatters.map((chatter, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-highlight)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold" style={{ color: i < 3 ? '#FFD700' : 'var(--text-muted)' }}>#{i + 1}</span>
                        <span className="text-sm" style={{ color: 'var(--text-main)' }}>{chatter.name}</span>
                      </div>
                      <span className="text-sm font-medium" style={{ color: 'var(--primary)' }}>{chatter.count} msgs</span>
                    </div>
                  )) : (
                    <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>No chat data yet</p>
                  )}
                </div>
              </div>

              {/* Top Donors */}
              <div className="glass rounded-2xl p-4">
                <h3 className="font-bold mb-4" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  <DollarSign className="w-5 h-5 inline mr-2" style={{ color: '#22C55E' }} />
                  Top Donors (All Time)
                </h3>
                <div className="space-y-2">
                  {analytics.top_donors.length > 0 ? analytics.top_donors.map((donor, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-highlight)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold" style={{ color: i < 3 ? '#FFD700' : 'var(--text-muted)' }}>#{i + 1}</span>
                        <span className="text-sm" style={{ color: 'var(--text-main)' }}>{donor.name}</span>
                      </div>
                      <span className="text-sm font-bold" style={{ color: '#22C55E' }}>${donor.total.toFixed(2)}</span>
                    </div>
                  )) : (
                    <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>No donation data yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Daily Registrations & Requests */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
                  <h3 className="font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    New Users (7 days)
                  </h3>
                </div>
                <div className="flex items-end gap-2 h-24">
                  {analytics.daily_registrations.map((day, i) => {
                    const max = Math.max(...analytics.daily_registrations.map(d => d.count), 1);
                    const height = (day.count / max) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full rounded-t transition-all"
                          style={{ 
                            height: `${height}%`, 
                            minHeight: day.count > 0 ? '4px' : '2px',
                            backgroundColor: 'var(--secondary)' 
                          }}
                          title={`${day.count} new users`}
                        />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{day.date.split(' ')[1]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Music className="w-5 h-5" style={{ color: '#A855F7' }} />
                  <h3 className="font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    Song Requests (7 days)
                  </h3>
                </div>
                <div className="flex items-end gap-2 h-24">
                  {analytics.daily_requests.map((day, i) => {
                    const max = Math.max(...analytics.daily_requests.map(d => d.count), 1);
                    const height = (day.count / max) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full rounded-t transition-all"
                          style={{ 
                            height: `${height}%`, 
                            minHeight: day.count > 0 ? '4px' : '2px',
                            backgroundColor: '#A855F7' 
                          }}
                          title={`${day.count} requests`}
                        />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{day.date.split(' ')[1]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="glass rounded-2xl overflow-hidden">
            <div className="p-4 border-b" style={{ borderColor: 'var(--surface-highlight)' }}>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                User Management ({users.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: 'var(--surface-highlight)' }}>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>User</th>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Email</th>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Role</th>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Joined</th>
                    <th className="text-right p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr 
                      key={u.user_id} 
                      className="border-b"
                      style={{ borderColor: 'var(--surface-highlight)', opacity: u.banned ? 0.5 : 1 }}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getRoleIcon(u.role)}
                          <span style={{ color: 'var(--text-main)' }}>{u.name}</span>
                          {u.banned && (
                            <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#EF4444', color: 'white' }}>
                              Banned
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm" style={{ color: 'var(--text-muted)' }}>{u.email}</td>
                      <td className="p-3">
                        {isAdmin ? (
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                            disabled={u.user_id === user.user_id || actionLoading === u.user_id}
                            className="px-2 py-1 rounded text-sm"
                            style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                          >
                            <option value="user">User</option>
                            <option value="dj">DJ</option>
                            <option value="mod">Mod</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <span className="text-sm capitalize" style={{ color: 'var(--text-main)' }}>{u.role}</span>
                        )}
                      </td>
                      <td className="p-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(u.created_at)}
                      </td>
                      <td className="p-3 text-right">
                        {isAdmin && u.user_id !== user.user_id && (
                          <button
                            onClick={() => handleBan(u.user_id, !u.banned)}
                            disabled={actionLoading === u.user_id}
                            className="p-2 rounded-lg transition-colors"
                            style={{ 
                              backgroundColor: u.banned ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                              color: u.banned ? '#22C55E' : '#EF4444'
                            }}
                            title={u.banned ? 'Unban' : 'Ban'}
                          >
                            {actionLoading === u.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : u.banned ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <Ban className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Donations Tab */}
        {activeTab === 'donations' && (
          <div className="glass rounded-2xl overflow-hidden">
            <div className="p-4 border-b" style={{ borderColor: 'var(--surface-highlight)' }}>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                Donation History ({donations.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: 'var(--surface-highlight)' }}>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Donor</th>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Amount</th>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Message</th>
                    <th className="text-left p-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((d) => (
                    <tr key={d.transaction_id} className="border-b" style={{ borderColor: 'var(--surface-highlight)' }}>
                      <td className="p-3" style={{ color: 'var(--text-main)' }}>{d.donor_name || 'Anonymous'}</td>
                      <td className="p-3">
                        <span className="font-bold" style={{ color: '#22C55E' }}>${d.amount.toFixed(2)}</span>
                      </td>
                      <td className="p-3">
                        <span 
                          className="text-xs px-2 py-1 rounded"
                          style={{ 
                            backgroundColor: d.status === 'paid' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                            color: d.status === 'paid' ? '#22C55E' : '#F59E0B'
                          }}
                        >
                          {d.status}
                        </span>
                      </td>
                      <td className="p-3 text-sm max-w-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        {d.message || '-'}
                      </td>
                      <td className="p-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(d.created_at)}
                      </td>
                    </tr>
                  ))}
                  {donations.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
                        No donations yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="glass rounded-2xl overflow-hidden">
            <div className="p-4 border-b" style={{ borderColor: 'var(--surface-highlight)' }}>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                Chat Messages ({messages.length})
              </h2>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {messages.map((m) => (
                <div 
                  key={m.message_id} 
                  className="flex items-start justify-between p-3 border-b"
                  style={{ borderColor: 'var(--surface-highlight)' }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getRoleIcon(m.user_role)}
                      <span className="font-medium text-sm" style={{ color: 'var(--text-main)' }}>
                        {m.user_name}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(m.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{m.text}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteMessage(m.message_id)}
                    disabled={actionLoading === m.message_id}
                    className="p-2 rounded-lg transition-colors ml-2"
                    style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}
                    title="Delete message"
                  >
                    {actionLoading === m.message_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
              {messages.length === 0 && (
                <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
                  No messages yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sponsors Tab */}
        {activeTab === 'sponsors' && (
          <div className="space-y-4">
            {/* Add/Edit Sponsor Form */}
            {showSponsorForm ? (
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    {editingSponsor ? 'Edit Sponsor' : 'Add New Sponsor'}
                  </h2>
                  <button
                    onClick={resetSponsorForm}
                    className="p-2 rounded-lg transition-colors hover:bg-white/10"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleSponsorSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Name *</label>
                    <input
                      type="text"
                      value={sponsorForm.name}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, name: e.target.value })}
                      required
                      className="w-full p-3 rounded-xl outline-none"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                      placeholder="Sponsor name"
                      data-testid="sponsor-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Tagline</label>
                    <input
                      type="text"
                      value={sponsorForm.tagline}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, tagline: e.target.value })}
                      className="w-full p-3 rounded-xl outline-none"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                      placeholder="Short tagline or slogan"
                      data-testid="sponsor-tagline-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Logo URL</label>
                    <input
                      type="url"
                      value={sponsorForm.logo_url}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, logo_url: e.target.value })}
                      className="w-full p-3 rounded-xl outline-none"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                      placeholder="https://example.com/logo.png"
                      data-testid="sponsor-logo-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Website URL</label>
                    <input
                      type="url"
                      value={sponsorForm.website_url}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, website_url: e.target.value })}
                      className="w-full p-3 rounded-xl outline-none"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                      placeholder="https://sponsor-website.com"
                      data-testid="sponsor-website-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={sponsorForm.is_active}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, is_active: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="sponsor-active-checkbox"
                    />
                    <label htmlFor="is_active" className="text-sm" style={{ color: 'var(--text-main)' }}>
                      Active (show in rotation)
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={actionLoading === 'sponsor-form'}
                      className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl font-medium transition-colors"
                      style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                      data-testid="sponsor-submit-btn"
                    >
                      {actionLoading === 'sponsor-form' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          {editingSponsor ? 'Update Sponsor' : 'Add Sponsor'}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={resetSponsorForm}
                      className="px-4 py-3 rounded-xl font-medium transition-colors"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-muted)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <button
                onClick={() => setShowSponsorForm(true)}
                className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl transition-colors"
                style={{ backgroundColor: 'var(--surface)', color: 'var(--primary)', border: '2px dashed var(--primary)' }}
                data-testid="add-sponsor-btn"
              >
                <Plus className="w-5 h-5" />
                Add New Sponsor
              </button>
            )}

            {/* Sponsors List */}
            <div className="glass rounded-2xl overflow-hidden">
              <div className="p-4 border-b" style={{ borderColor: 'var(--surface-highlight)' }}>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  <Megaphone className="w-5 h-5 inline mr-2" style={{ color: 'var(--primary)' }} />
                  Sponsors ({sponsors.length})
                </h2>
              </div>
              <div className="divide-y" style={{ borderColor: 'var(--surface-highlight)' }}>
                {sponsors.map((sponsor) => (
                  <div 
                    key={sponsor.sponsor_id} 
                    className="flex items-center justify-between p-4"
                    style={{ opacity: sponsor.is_active ? 1 : 0.5 }}
                    data-testid={`sponsor-${sponsor.sponsor_id}`}
                  >
                    <div className="flex items-center gap-4">
                      {sponsor.logo_url ? (
                        <img 
                          src={sponsor.logo_url} 
                          alt={sponsor.name}
                          className="w-12 h-12 rounded-lg object-cover"
                          style={{ backgroundColor: 'var(--surface-highlight)' }}
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: 'var(--surface-highlight)' }}
                        >
                          <Megaphone className="w-6 h-6" style={{ color: 'var(--text-muted)' }} />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium" style={{ color: 'var(--text-main)' }}>{sponsor.name}</h3>
                          {!sponsor.is_active && (
                            <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#F59E0B' }}>
                              Inactive
                            </span>
                          )}
                        </div>
                        {sponsor.tagline && (
                          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sponsor.tagline}</p>
                        )}
                        {sponsor.website_url && (
                          <a 
                            href={sponsor.website_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs flex items-center gap-1 mt-1 hover:underline"
                            style={{ color: 'var(--primary)' }}
                          >
                            <ExternalLink className="w-3 h-3" />
                            {sponsor.website_url}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleSponsorActive(sponsor)}
                        disabled={actionLoading === sponsor.sponsor_id}
                        className="p-2 rounded-lg transition-colors"
                        style={{ 
                          backgroundColor: sponsor.is_active ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                          color: sponsor.is_active ? '#F59E0B' : '#22C55E'
                        }}
                        title={sponsor.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {actionLoading === sponsor.sponsor_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : sponsor.is_active ? (
                          <Ban className="w-4 h-4" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditSponsor(sponsor)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }}
                        title="Edit"
                        data-testid={`edit-sponsor-${sponsor.sponsor_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSponsor(sponsor.sponsor_id)}
                        disabled={actionLoading === sponsor.sponsor_id}
                        className="p-2 rounded-lg transition-colors"
                        style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}
                        title="Delete"
                        data-testid={`delete-sponsor-${sponsor.sponsor_id}`}
                      >
                        {actionLoading === sponsor.sponsor_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
                {sponsors.length === 0 && (
                  <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
                    <Megaphone className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No sponsors yet</p>
                    <p className="text-sm">Add your first sponsor to display ads in the player</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && (
          <div className="space-y-4">
            {/* Add/Edit News Form */}
            {showNewsForm ? (
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    {editingNews ? 'Edit News Item' : 'Add News Item'}
                  </h2>
                  <button
                    onClick={resetNewsForm}
                    className="p-2 rounded-lg transition-colors hover:bg-white/10"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleNewsSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-muted)' }}>News Text *</label>
                    <input
                      type="text"
                      value={newsForm.text}
                      onChange={(e) => setNewsForm({ ...newsForm, text: e.target.value })}
                      required
                      className="w-full p-3 rounded-xl outline-none"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                      placeholder="Breaking: New DJ joining this Friday!"
                      data-testid="news-text-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Link URL (Optional)</label>
                    <input
                      type="url"
                      value={newsForm.link_url}
                      onChange={(e) => setNewsForm({ ...newsForm, link_url: e.target.value })}
                      className="w-full p-3 rounded-xl outline-none"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                      placeholder="https://example.com/article"
                      data-testid="news-link-input"
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Priority</label>
                      <input
                        type="number"
                        value={newsForm.priority}
                        onChange={(e) => setNewsForm({ ...newsForm, priority: parseInt(e.target.value) || 0 })}
                        className="w-full p-3 rounded-xl outline-none"
                        style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                        placeholder="0"
                        data-testid="news-priority-input"
                      />
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Higher = shows first</p>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        type="checkbox"
                        id="news_active"
                        checked={newsForm.is_active}
                        onChange={(e) => setNewsForm({ ...newsForm, is_active: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="news-active-checkbox"
                      />
                      <label htmlFor="news_active" className="text-sm" style={{ color: 'var(--text-main)' }}>
                        Active
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={actionLoading === 'news-form'}
                      className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl font-medium transition-colors"
                      style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                      data-testid="news-submit-btn"
                    >
                      {actionLoading === 'news-form' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          {editingNews ? 'Update' : 'Add News'}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={resetNewsForm}
                      className="px-4 py-3 rounded-xl font-medium transition-colors"
                      style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-muted)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <button
                onClick={() => setShowNewsForm(true)}
                className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl transition-colors"
                style={{ backgroundColor: 'var(--surface)', color: 'var(--primary)', border: '2px dashed var(--primary)' }}
                data-testid="add-news-btn"
              >
                <Plus className="w-5 h-5" />
                Add News Item
              </button>
            )}

            {/* RSS Feed Section */}
            <div className="glass rounded-2xl p-4">
              <h3 className="font-bold mb-3" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                <RefreshCw className="w-4 h-4 inline mr-2" style={{ color: 'var(--secondary)' }} />
                RSS Feed (Auto-Import)
              </h3>
              <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                Automatically pull news from an RSS feed
              </p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={rssUrl}
                  onChange={(e) => setRssUrl(e.target.value)}
                  className="flex-1 p-3 rounded-xl outline-none font-mono text-sm"
                  style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                  placeholder="https://example.com/feed.xml"
                  data-testid="rss-url-input"
                />
                <button
                  onClick={handlePreviewRss}
                  disabled={!rssUrl || rssLoading}
                  className="px-4 py-2 rounded-xl font-medium transition-colors"
                  style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                >
                  {rssLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}
                </button>
                <button
                  onClick={handleSaveRssUrl}
                  disabled={actionLoading === 'rss-save'}
                  className="px-4 py-2 rounded-xl font-medium transition-colors"
                  style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                >
                  {actionLoading === 'rss-save' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
              </div>
              
              {rssPreview?.saved && (
                <div className="mt-2 p-2 rounded-lg flex items-center gap-2" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                  <CheckCircle className="w-4 h-4" style={{ color: '#22C55E' }} />
                  <span className="text-sm" style={{ color: '#22C55E' }}>RSS URL saved!</span>
                </div>
              )}
              
              {rssPreview?.error && (
                <div className="mt-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}>
                  <span className="text-sm" style={{ color: '#EF4444' }}>{rssPreview.error}</span>
                </div>
              )}
              
              {rssPreview?.items?.length > 0 && (
                <div className="mt-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--surface-highlight)' }}>
                  <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-main)' }}>
                    Feed: {rssPreview.feed_title}
                  </p>
                  <div className="space-y-2">
                    {rssPreview.items.map((item, i) => (
                      <div key={i} className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        • {item.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* News List */}
            <div className="glass rounded-2xl overflow-hidden">
              <div className="p-4 border-b" style={{ borderColor: 'var(--surface-highlight)' }}>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                  <Newspaper className="w-5 h-5 inline mr-2" style={{ color: 'var(--primary)' }} />
                  News Ticker Items ({newsItems.length})
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  These items scroll at the bottom of the player
                </p>
              </div>
              <div className="divide-y" style={{ borderColor: 'var(--surface-highlight)' }}>
                {newsItems.map((news) => (
                  <div 
                    key={news.news_id} 
                    className="flex items-center justify-between p-4"
                    style={{ opacity: news.is_active ? 1 : 0.5 }}
                    data-testid={`news-${news.news_id}`}
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-muted)' }}>
                          #{news.priority}
                        </span>
                        {!news.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#F59E0B' }}>
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="mt-1 truncate" style={{ color: 'var(--text-main)' }}>{news.text}</p>
                      {news.link_url && (
                        <a 
                          href={news.link_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs flex items-center gap-1 mt-1 hover:underline"
                          style={{ color: 'var(--primary)' }}
                        >
                          <ExternalLink className="w-3 h-3" />
                          {news.link_url}
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleNewsActive(news)}
                        disabled={actionLoading === news.news_id}
                        className="p-2 rounded-lg transition-colors"
                        style={{ 
                          backgroundColor: news.is_active ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                          color: news.is_active ? '#F59E0B' : '#22C55E'
                        }}
                        title={news.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {actionLoading === news.news_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : news.is_active ? (
                          <Ban className="w-4 h-4" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditNews(news)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }}
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteNews(news.news_id)}
                        disabled={actionLoading === news.news_id}
                        className="p-2 rounded-lg transition-colors"
                        style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}
                        title="Delete"
                      >
                        {actionLoading === news.news_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
                {newsItems.length === 0 && (
                  <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
                    <Newspaper className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No news items yet</p>
                    <p className="text-sm">Add news to display in the scrolling ticker</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab - Station Configuration */}
        {activeTab === 'settings' && stationConfig && (
          <div className="max-w-2xl mx-auto">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    Station Settings
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Configure your radio station details
                  </p>
                </div>
              </div>

              {configSaved && (
                <div className="mb-4 p-3 rounded-xl flex items-center gap-2" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                  <CheckCircle className="w-5 h-5" style={{ color: '#22C55E' }} />
                  <span style={{ color: '#22C55E' }}>Settings saved successfully!</span>
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 rounded-xl flex items-center gap-2" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}>
                  <AlertCircle className="w-5 h-5" style={{ color: '#EF4444' }} />
                  <span style={{ color: '#EF4444' }}>{error}</span>
                </div>
              )}

              <form onSubmit={handleSaveStationConfig} className="space-y-4">
                {/* Station Name */}
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <Radio className="w-4 h-4" />
                    Station Name *
                  </label>
                  <input
                    type="text"
                    value={stationConfig.station_name}
                    onChange={(e) => setStationConfig({ ...stationConfig, station_name: e.target.value })}
                    required
                    className="w-full p-3 rounded-xl outline-none"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="My Radio Station"
                    data-testid="station-name-input"
                  />
                </div>

                {/* Station Description */}
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <MessageSquare className="w-4 h-4" />
                    Station Description
                  </label>
                  <textarea
                    value={stationConfig.station_description}
                    onChange={(e) => setStationConfig({ ...stationConfig, station_description: e.target.value })}
                    rows={2}
                    className="w-full p-3 rounded-xl outline-none resize-none"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="Your station tagline or description"
                    data-testid="station-description-input"
                  />
                </div>

                {/* Stream URL */}
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <Link className="w-4 h-4" />
                    Stream URL (MP3) *
                  </label>
                  <input
                    type="url"
                    value={stationConfig.stream_url}
                    onChange={(e) => setStationConfig({ ...stationConfig, stream_url: e.target.value })}
                    required
                    className="w-full p-3 rounded-xl outline-none font-mono text-sm"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="https://yourstream.com:8000/radio.mp3"
                    data-testid="stream-url-input"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    Main audio stream URL (MP3 or AAC format)
                  </p>
                </div>

                {/* Logo URL */}
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <Image className="w-4 h-4" />
                    Logo URL
                  </label>
                  <input
                    type="url"
                    value={stationConfig.logo_url}
                    onChange={(e) => setStationConfig({ ...stationConfig, logo_url: e.target.value })}
                    className="w-full p-3 rounded-xl outline-none font-mono text-sm"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="https://example.com/logo.png"
                    data-testid="logo-url-input"
                  />
                  {stationConfig.logo_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img 
                        src={stationConfig.logo_url} 
                        alt="Logo preview" 
                        className="w-12 h-12 rounded-lg object-cover"
                        style={{ backgroundColor: 'var(--surface-highlight)' }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Preview</span>
                    </div>
                  )}
                </div>

                {/* HLS URL */}
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <Music className="w-4 h-4" />
                    HLS URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={stationConfig.hls_url || ''}
                    onChange={(e) => setStationConfig({ ...stationConfig, hls_url: e.target.value })}
                    className="w-full p-3 rounded-xl outline-none font-mono text-sm"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="https://yourstream.com/hls/live.m3u8"
                    data-testid="hls-url-input"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    HLS stream for better mobile support (m3u8 format)
                  </p>
                </div>

                {/* Video URL */}
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <Video className="w-4 h-4" />
                    Video/Owncast URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={stationConfig.video_url || ''}
                    onChange={(e) => setStationConfig({ ...stationConfig, video_url: e.target.value })}
                    className="w-full p-3 rounded-xl outline-none font-mono text-sm"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="https://live.yourstation.com"
                    data-testid="video-url-input"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    Owncast or video stream URL for live video feed
                  </p>
                </div>

                {/* Station API URL */}
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    <RefreshCw className="w-4 h-4" />
                    Station API URL (For Now Playing data)
                  </label>
                  <input
                    type="url"
                    value={stationConfig.station_api_url || ''}
                    onChange={(e) => setStationConfig({ ...stationConfig, station_api_url: e.target.value })}
                    className="w-full p-3 rounded-xl outline-none font-mono text-sm"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="https://yourstream.com/api/nowplaying/station"
                    data-testid="station-api-url-input"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    API endpoint for fetching now playing info & album art (AzuraCast format)
                  </p>
                </div>

                {/* About Section */}
                <div className="pt-4 border-t" style={{ borderColor: 'var(--surface-highlight)' }}>
                  <h3 className="font-bold mb-3" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    About Section
                  </h3>
                  <label className="flex items-center gap-2 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                    About Text
                  </label>
                  <textarea
                    value={stationConfig.about_text || ''}
                    onChange={(e) => setStationConfig({ ...stationConfig, about_text: e.target.value })}
                    rows={4}
                    className="w-full p-3 rounded-xl outline-none resize-none"
                    style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                    placeholder="Tell your listeners about your station, history, mission, etc."
                    data-testid="about-text-input"
                  />
                </div>

                {/* Social Media Links */}
                <div className="pt-4 border-t" style={{ borderColor: 'var(--surface-highlight)' }}>
                  <h3 className="font-bold mb-3" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
                    Social Media Links
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm mb-1 block" style={{ color: 'var(--text-muted)' }}>Twitter/X</label>
                      <input
                        type="url"
                        value={stationConfig.social_twitter || ''}
                        onChange={(e) => setStationConfig({ ...stationConfig, social_twitter: e.target.value })}
                        className="w-full p-2 rounded-lg outline-none text-sm"
                        style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                        placeholder="https://twitter.com/yourstation"
                      />
                    </div>
                    <div>
                      <label className="text-sm mb-1 block" style={{ color: 'var(--text-muted)' }}>Facebook</label>
                      <input
                        type="url"
                        value={stationConfig.social_facebook || ''}
                        onChange={(e) => setStationConfig({ ...stationConfig, social_facebook: e.target.value })}
                        className="w-full p-2 rounded-lg outline-none text-sm"
                        style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                        placeholder="https://facebook.com/yourstation"
                      />
                    </div>
                    <div>
                      <label className="text-sm mb-1 block" style={{ color: 'var(--text-muted)' }}>Instagram</label>
                      <input
                        type="url"
                        value={stationConfig.social_instagram || ''}
                        onChange={(e) => setStationConfig({ ...stationConfig, social_instagram: e.target.value })}
                        className="w-full p-2 rounded-lg outline-none text-sm"
                        style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                        placeholder="https://instagram.com/yourstation"
                      />
                    </div>
                    <div>
                      <label className="text-sm mb-1 block" style={{ color: 'var(--text-muted)' }}>YouTube</label>
                      <input
                        type="url"
                        value={stationConfig.social_youtube || ''}
                        onChange={(e) => setStationConfig({ ...stationConfig, social_youtube: e.target.value })}
                        className="w-full p-2 rounded-lg outline-none text-sm"
                        style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                        placeholder="https://youtube.com/@yourstation"
                      />
                    </div>
                    <div>
                      <label className="text-sm mb-1 block" style={{ color: 'var(--text-muted)' }}>TikTok</label>
                      <input
                        type="url"
                        value={stationConfig.social_tiktok || ''}
                        onChange={(e) => setStationConfig({ ...stationConfig, social_tiktok: e.target.value })}
                        className="w-full p-2 rounded-lg outline-none text-sm"
                        style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
                        placeholder="https://tiktok.com/@yourstation"
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <button
                  type="submit"
                  disabled={actionLoading === 'station-config'}
                  className="w-full flex items-center justify-center gap-2 p-4 rounded-xl font-medium transition-colors mt-6"
                  style={{ backgroundColor: 'var(--primary)', color: 'white' }}
                  data-testid="save-station-config-btn"
                >
                  {actionLoading === 'station-config' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Save Station Settings
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
